/**
 * The late-proof approval link (Ken, 10-20): `https://design.litko.net/approve#<secret>`.
 *
 * The secret sits in the URL FRAGMENT, which a browser never sends to a server,
 * so it reaches no request log, proxy or Referer; the page sends it only in a
 * POST body. The secret is handed out once (the server keeps its hash), so the
 * designer remembers the link on this device and offers it on a return visit —
 * the customer's own secret, in their own browser (named for OMAR).
 *
 * ⚠ The `/approve#<secret>` shape is a contract with Redtail's email (its URL
 * builder is pinned by a back-end test): change the two together.
 */

const SECRET = /^[A-Za-z0-9_-]{43}$/;
const STORE_KEY = 'tf.approvalLinks';

/**
 * Which standalone page a URL opens. The approval page is chosen from the URL
 * alone — never from the cart switch or any other flag — so it stays up when
 * the studio's cart is off (condition 1; pinned in test/approval.test.ts).
 */
export type StandalonePage = { kind: 'approval'; secret: string | null } | { kind: 'other' };

export function standalonePage(pathname: string, hash: string): StandalonePage {
    return isApprovalPath(pathname) ? { kind: 'approval', secret: secretFromHash(hash) } : { kind: 'other' };
}

/** Is this the approval page's path? (`/approve`, with or without a trailing slash.) */
export function isApprovalPath(pathname: string): boolean {
    return /^\/approve\/?$/.test(pathname);
}

/** The secret in an approval page's `#fragment`, or null when absent or malformed. */
export function secretFromHash(hash: string): string | null {
    const raw = hash.startsWith('#') ? hash.slice(1) : hash;
    return SECRET.test(raw) ? raw : null;
}

export interface RememberedLink {
    url: string;
    reorderCode: string | null;
    savedAt: string;
}

/**
 * A link approved on another device is never forgotten here, so links older
 * than this stop being offered (a pack waits days, not months).
 */
const REMEMBER_DAYS = 30;

/** Links remembered on this device; empty when storage is blocked or holds junk. */
export function rememberedApprovalLinks(): RememberedLink[] {
    try {
        const parsed: unknown = JSON.parse(window.localStorage.getItem(STORE_KEY) ?? '[]');
        const cutoff = Date.now() - REMEMBER_DAYS * 86_400_000;
        return Array.isArray(parsed)
            ? parsed.filter(
                  (l): l is RememberedLink =>
                      typeof l === 'object' && l !== null && typeof (l as RememberedLink).url === 'string'
                      && Date.parse((l as RememberedLink).savedAt) >= cutoff,
              )
            : [];
    } catch {
        return [];
    }
}

export function rememberApprovalLink(url: string, reorderCode: string | null): void {
    try {
        const links = rememberedApprovalLinks().filter((l) => l.url !== url);
        links.push({ url, reorderCode, savedAt: new Date().toISOString() });
        window.localStorage.setItem(STORE_KEY, JSON.stringify(links.slice(-10)));
    } catch {
        // Storage blocked (private window): the link is still on screen and emailed.
    }
}

/** Forget a link once every pack it names is confirmed or closed. */
export function forgetApprovalLink(secret: string): void {
    try {
        const links = rememberedApprovalLinks().filter((l) => !l.url.endsWith(`#${secret}`));
        window.localStorage.setItem(STORE_KEY, JSON.stringify(links));
    } catch {
        // Nothing to do.
    }
}
